import { Access, EmployeeType } from '@prisma/client';
import { Space, Typography } from 'antd';
import { ColumnsType } from 'antd/es/table';

import {
  AgsEntitlementAccess,
  IemAccess,
  isAgsEntitlementAccess,
  isAgsRoleAccess,
  SourceAccessItem,
} from '@app/domain-access-interfaces';
import { Tables } from '@app/web-components';

import { EmployeeTypeRenderer, useAccessSelectTable } from '../..';

import { AccessSelectModal } from './access-select-modal';

export const accessSelectColumns: ColumnsType<SourceAccessItem | Access> = [
  {
    title: 'Source',
    dataIndex: 'accessSource',
    width: 120,
  },
  {
    title: 'Emp Type',
    dataIndex: 'employeeType',
    width: 120,
    render: (employeeType: EmployeeType) => <EmployeeTypeRenderer employeeType={employeeType} />,
  },
  {
    title: 'Name',
    dataIndex: 'name',
    render: (_, record) => {
      if (isAgsRoleAccess(record) || isAgsEntitlementAccess(record)) {
        return (
          <>
            <div>{record.name}</div>
            {record.name && record.meta?.displayName && record.name !== record.meta.displayName && (
              <Typography.Text type="secondary">a.k.a. {record.meta.displayName}</Typography.Text>
            )}
          </>
        );
      }

      return record.name;
    },
  },
  {
    title: 'Description',
    dataIndex: 'description',
    render: (_, record) => {
      const unixGroups = (
        isAgsRoleAccess(record) && record.meta
          ? record.meta.entitlements.flatMap((x) => x.unixGroups ?? [])
          : (record as AgsEntitlementAccess | IemAccess).meta?.unixGroups ?? []
      ).join(', ');

      return (
        <>
          <div>{record.description}</div>
          {!!unixGroups && <Typography.Text type="secondary">Unix Groups: {unixGroups}</Typography.Text>}
        </>
      );
    },
  },
];

export type AccessSelectProps = Omit<
  Tables.SelectTableProps<SourceAccessItem | Access, SourceAccessItem | Access>,
  'onChange'
> & {
  agsOnly?: boolean;
  value?: (SourceAccessItem | Access) | (SourceAccessItem | Access)[];
  onChange?: (value?: (SourceAccessItem | Access) | (SourceAccessItem | Access)[]) => void;
};

export const AccessSelect = ({
  actions,
  agsOnly,
  columns = accessSelectColumns,
  disabled,
  mode = 'multiple',
  value: _value,
  onChange,
  ...props
}: AccessSelectProps) => {
  const { getProps } = useAccessSelectTable();

  const value = Array.isArray(_value) ? _value : _value ? [_value] : [];

  const handleOnChange = (newValue: (SourceAccessItem | Access)[]) => {
    onChange && (mode === 'multiple' ? onChange(newValue) : onChange(newValue[0]));
  };

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <AccessSelectModal agsOnly={agsOnly} disabled={disabled} mode={mode} value={value} onChange={handleOnChange} />
      <Tables.SelectTable
        {...getProps({ actions, columns, dataSource: value, value, onChange: handleOnChange, ...props })}
      />
    </Space>
  );
};
